import React from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, Mail, Phone, MapPin } from "lucide-react";
import { useI18n } from "@/i18n";

export default function Footer() {
  const { t, lang } = useI18n();
  const base = `/${lang}`;

  return (
    <footer className="bg-[#0B2545] text-slate-200 border-t-4 border-[#006837]" data-testid="site-footer">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-14">
        <div className="grid gap-10 md:grid-cols-2 lg:grid-cols-4">
          {/* Brand */}
          <div>
            <Link to={base} className="flex items-center gap-3" data-testid="footer-logo">
              <span className="grid place-items-center h-10 w-10 rounded-md bg-[#006837] text-white">
                <ShieldCheck className="h-6 w-6" aria-hidden="true" />
              </span>
              <span className="flex flex-col leading-tight">
                <span className="font-serif-heading text-lg font-bold text-white">
                  {t("common.companyName")}
                </span>
                <span className="text-[11px] uppercase tracking-widest text-[#8DA9C4]">
                  {t("common.brandTagline")}
                </span>
              </span>
            </Link>
            <p className="mt-4 text-sm text-slate-300 leading-relaxed">{t("footer.tagline")}</p>
          </div>

          {/* Sitemap */}
          <div>
            <h3 className="text-xs uppercase tracking-widest text-[#8DA9C4] font-semibold">
              {t("footer.colSitemap")}
            </h3>
            <ul className="mt-4 space-y-2 text-sm">
              <li>
                <a href={`${base}`} data-testid="footer-link-home" className="pn-link-light">
                  {t("footer.linkHome")}
                </a>
              </li>
              <li>
                <a href={`${base}#benefits`} data-testid="footer-link-benefits" className="pn-link-light">
                  {t("footer.linkBenefits")}
                </a>
              </li>
              <li>
                <a href={`${base}#how-it-works`} data-testid="footer-link-how" className="pn-link-light">
                  {t("footer.linkHowItWorks")}
                </a>
              </li>
              <li>
                <a href={`${base}#faq`} data-testid="footer-link-faq" className="pn-link-light">
                  {t("footer.linkFaq")}
                </a>
              </li>
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h3 className="text-xs uppercase tracking-widest text-[#8DA9C4] font-semibold">
              {t("footer.colLegal")}
            </h3>
            <ul className="mt-4 space-y-2 text-sm">
              <li>
                <Link to={`${base}/privacy-policy`} data-testid="footer-link-privacy" className="pn-link-light">
                  {t("footer.linkPrivacy")}
                </Link>
              </li>
              <li>
                <Link to={`${base}/cookie-policy`} data-testid="footer-link-cookies" className="pn-link-light">
                  {t("footer.linkCookies")}
                </Link>
              </li>
              <li>
                <Link to={`${base}/terms`} data-testid="footer-link-terms" className="pn-link-light">
                  {t("footer.linkTerms")}
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="text-xs uppercase tracking-widest text-[#8DA9C4] font-semibold">
              {t("footer.colContact")}
            </h3>
            <ul className="mt-4 space-y-3 text-sm text-slate-300">
              <li className="flex gap-3">
                <MapPin className="h-4 w-4 mt-0.5 text-[#8DA9C4]" aria-hidden="true" />
                <span data-testid="footer-address">{t("footer.contactAddress")}</span>
              </li>
              <li className="flex gap-3">
                <Mail className="h-4 w-4 mt-0.5 text-[#8DA9C4]" aria-hidden="true" />
                <a
                  href={`mailto:${t("footer.contactEmail")}`}
                  data-testid="footer-email"
                  className="pn-link-light"
                >
                  {t("footer.contactEmail")}
                </a>
              </li>
              <li className="flex gap-3">
                <Phone className="h-4 w-4 mt-0.5 text-[#8DA9C4]" aria-hidden="true" />
                <a
                  href={`tel:${t("footer.contactPhone").replace(/\s|\(|\)|-/g, "")}`}
                  data-testid="footer-phone"
                  className="pn-link-light"
                >
                  {t("footer.contactPhone")}
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Legal block */}
        <div className="mt-12 border-t border-white/10 pt-8 grid gap-3 text-xs text-slate-400 leading-relaxed" data-testid="footer-legal">
          <p className="font-semibold text-slate-200 uppercase tracking-wider text-[11px]">
            {t("footer.legalHeading")}
          </p>
          <p>{t("footer.legalCompany")}</p>
          <p>{t("footer.legalRfc")}</p>
          <p>{t("footer.legalRegistry")}</p>
          <p>{t("footer.legalLicense")}</p>
          <p>{t("footer.legalJurisdiction")}</p>
          <p>{t("footer.legalYear")}</p>
          <p className="mt-2 italic text-slate-500">{t("footer.legalDisclaimer")}</p>
        </div>

        <div className="mt-8 flex flex-col md:flex-row justify-between gap-3 text-xs text-slate-400">
          <p data-testid="footer-copyright">{t("footer.copyright")}</p>
          <p>RFC · PAY260115MX8 · CDMX, México</p>
        </div>
      </div>
    </footer>
  );
}
